import { ActivityDashboardService } from './activity-dashboard.service';
import {
  ActivityChartData,
  ActivityChartsDataType,
  ActivityDashboardWidget,
  BehaviorChartsDataType,
  BehaviorHeaderStats,
  ChartQueryParams,
  DashboardBehaviorCaseList,
  DashboardBehaviorCaseListQueryParams,
  DistrictUsageCaseList,
  DistrictUsageCaseListQueryParams,
  DistrictUsageHeaderStats,
  DistrictUsageSummary,
  DistrictUsageSummaryPayload,
  DistricUsageChartsDataType,
  ELearningChartDataType,
  ELearningDataList,
  ELearningDataListQueryParams,
  ELearningHeaderStats,
  WidgetPayload,
  WidgetReorderPayload,
} from './activity-dashboard.interface';
import {
  ChangeDetectorRef,
  EventEmitter,
  inject,
  Output,
  Component,
} from '@angular/core';
import { LoaderService } from '../../core/services/loader.service';
import { CommonService } from '../../shared/services/common.service';
import { AuthService } from '../../core/services/auth.service';
import {
  OrganizationDetailModel,
  userDetailModel,
} from '../organizations/organization.interface';
import { Observable, of } from 'rxjs';
import {
  DashboardActivityChartType,
  DashboardActivityComponent,
  DashboardBehaviorManagementComponent,
  DashboardDistrictUsageComponent,
  DashboardElearningComponent,
  DashboardType,
  ELearningChartType,
  LmsChartType,
} from './activity-dashboard.enums';

@Component({
  selector: 'app-dashboard-common',
  template: '',
})
export class DashboardCommonComponent {
  readonly activityDashboardService = inject(ActivityDashboardService);
  readonly loaderService = inject(LoaderService);
  readonly commonService = inject(CommonService);
  readonly authService = inject(AuthService);
  readonly cdr = inject(ChangeDetectorRef);

  @Output() changeFilter = new EventEmitter<BehaviorChartsDataType | null>();
  @Output() changeFilterActivity =
    new EventEmitter<ActivityChartsDataType | null>();

  userData: userDetailModel | null = null;
  orgData: OrganizationDetailModel | null = null;
  userRoles: string[] = [];
  orgId = 0;
  selectedOrg = 0;
  dashboardType = 26;

  userSearch = 0;
  studentSearch = 0;
  learnerSearch = 0;
  startDate = '';
  endDate = '';

  protected toLocalDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  widgetList: ActivityDashboardWidget[] = [];
  loading$: Observable<boolean> = of(false);
  totalRecords = 0;
  totalDistricRecords = 0;
  totalELearningRecords = 0;

  behaviorHeaderStats: BehaviorHeaderStats | null = null;
  districtUsageHeaderStats: DistrictUsageHeaderStats | null = null;
  eLearningHeaderStats: ELearningHeaderStats | null = null;
  districUsageSummary: DistrictUsageSummary | null = null;

  BehaviorChartsData: BehaviorChartsDataType | null = {
    VolumeOfBehaviorCategories: [],
    TeamMembersSupportingCasesTop10: [],
    CaseVolumeTop10: [],
    VolumeOfLoginsPerSchoolTop10: [],
    BehaviorScreener: [],
    CaseActivity: [],
    AgeRange: [],
    CasesByRace: [],
    CasesByGeneralEducation: [],
    CasesBySpecialEducation: [],
    Top10ImplementedStrategies: [],
    FunctionsByBehavior: [],
    CaseData: [],
  };
  DistricUsageChartsData: DistricUsageChartsDataType | null = {
    VolumeOfBehaviorsByOrganizationTop7: [],
    HoursOfPDByOrganizationTop7: [],
    DataCollectionByOrganizationTop7: [],
    OrganizationsWithTopEnrollmentTop7: [],
  };
  ELearningChartsData: ELearningChartDataType | null = {
    HoursOfPDByOrganizationTop7: [],
    WorkshopsWithExpiredDeadlinesTop7Organizations: [],
    CompletedWorkshops: [],
  };
  ActivityDashboardChartsData: ActivityChartsDataType | null = {
    LoginActivity: {} as ActivityChartData,
    TotalLoginActivity: {} as ActivityChartData,
    DataCollectionActivity: {} as ActivityChartData,
    TotalDataPointsByTypeEntered: {} as ActivityChartData,
    WorkshopActivity: {} as ActivityChartData,
    TopDistinctLoginsByDistrict: {} as ActivityChartData,
    TopNonDistinctLoginsByDistrict: {} as ActivityChartData,
    TopBehaviorCasesByType: {} as ActivityChartData,
    TotalCollectedPoints: {} as ActivityChartData,
    TopWorkshopsCompleted: {} as ActivityChartData,
    TopWorkshopsAccessed: {} as ActivityChartData,
    TopQuizzesPassed: {} as ActivityChartData,
    TopQuizzesAccessed: {} as ActivityChartData,
    TotalWorkshopsQuizzesCompleted: {} as ActivityChartData,
  };

  behaviorCaseList: DashboardBehaviorCaseList[] = [];
  districtUsageCaseList: DistrictUsageCaseList[] = [];
  eLearningDataList: ELearningDataList[] = [];

  readonly lmsActivityChartTypes = [
    DashboardActivityComponent.WorkshopActivity,
    DashboardActivityComponent.TopWorkshopsCompleted,
    DashboardActivityComponent.TopWorkshopsAccessed,
    DashboardActivityComponent.TopQuizzesAccessed,
    DashboardActivityComponent.TopQuizzesPassed,
    DashboardActivityComponent.TotalWorkshopsQuizzesCompleted,
  ];

  constructor() {
    this.loading$ = this.loaderService.loading$;
    this.authService.userData$.subscribe(user => (this.userData = user));
    this.authService.userRoles$.subscribe(roles => (this.userRoles = roles));
    this.activityDashboardService.orgnizationId$.subscribe(
      val => (this.selectedOrg = val)
    );
    this.activityDashboardService.userId$.subscribe(
      val => (this.userSearch = val)
    );
    this.activityDashboardService.studentId$.subscribe(
      val => (this.studentSearch = val)
    );
    this.activityDashboardService.leanerId$.subscribe(
      val => (this.learnerSearch = val)
    );
    this.activityDashboardService.dashboardId$.subscribe(
      val => (this.dashboardType = val)
    );
    this.activityDashboardService.dateRange$.subscribe(val => {
      this.startDate = val[0] ? this.toLocalDateString(val[0]) : '';
      this.endDate = val[1] ? this.toLocalDateString(val[1]) : '';
    });
  }

  updateFilter(dashboardId?: number) {
    this.getWidgetList(
      this.userData?.id ?? 0,
      dashboardId ?? this.dashboardType
    );
    if (dashboardId === DashboardType.BehaviorManagementOverview) {
      this.getBehavoirHeaderStats();
      this.getBehaviorCaseList(10, 1, '', undefined);
    }
    if (dashboardId === DashboardType.DistrictUsage) {
      this.getDistricUsageHeaderStats();
      this.getDistricUsageSummary();
      this.getDistrictUsageCaseList(10, 1, '', undefined);
    }
    if (dashboardId === DashboardType.ELearningImplementationOverview) {
      this.getELEarningSummary();
      this.getELearningDataList(10, 1, 'org_name', 0);
    }
  }

  getWidgetList(userId: number, dashboardType: number) {
    if (dashboardType !== 0) {
      this.widgetList = [];
      this.loaderService.show();
      this.activityDashboardService
        .getDashboardWidgets(userId, dashboardType)
        .subscribe({
          next: response => {
            if (response?.result) {
              this.widgetList = response.result;
            }
            this.loaderService.hide();
            this.cdr.detectChanges();
            if (this.widgetList.length > 0) {
              this.reserChartData();
              this.widgetList.forEach(widget => {
                if (widget.statusId !== 1) {
                  widget.isLoading = false;
                  return;
                }
                widget.isLoading = true;
                if (
                  dashboardType === DashboardType.BehaviorManagementOverview
                ) {
                  this.processBehvaiorOverviewChartsData(widget);
                } else if (dashboardType === DashboardType.DistrictUsage) {
                  this.processDistricUsageChartsData(widget);
                } else if (
                  dashboardType ===
                  DashboardType.ELearningImplementationOverview
                ) {
                  this.processELEarningChartsData(widget);
                } else {
                  this.processActivityChartsData(widget);
                }
              });
            }
          },
          error: error => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    }
  }

  reserChartData() {
    if (this.dashboardType === DashboardType.BehaviorManagementOverview) {
      this.BehaviorChartsData = {
        VolumeOfBehaviorCategories: [],
        TeamMembersSupportingCasesTop10: [],
        CaseVolumeTop10: [],
        VolumeOfLoginsPerSchoolTop10: [],
        BehaviorScreener: [],
        CaseActivity: [],
        AgeRange: [],
        CasesByRace: [],
        CasesByGeneralEducation: [],
        CasesBySpecialEducation: [],
        Top10ImplementedStrategies: [],
        FunctionsByBehavior: [],
        CaseData: [],
      };
      this.changeFilter.emit(null);
    } else if (this.dashboardType === DashboardType.DistrictUsage) {
      this.DistricUsageChartsData = {
        VolumeOfBehaviorsByOrganizationTop7: [],
        HoursOfPDByOrganizationTop7: [],
        DataCollectionByOrganizationTop7: [],
        OrganizationsWithTopEnrollmentTop7: [],
      };
      this.changeFilter.emit(null);
    } else if (
      this.dashboardType === DashboardType.ELearningImplementationOverview
    ) {
      this.ELearningChartsData = {
        HoursOfPDByOrganizationTop7: [],
        WorkshopsWithExpiredDeadlinesTop7Organizations: [],
        CompletedWorkshops: [],
      };
      this.changeFilter.emit(null);
    } else {
      this.ActivityDashboardChartsData = {
        LoginActivity: {} as ActivityChartData,
        TotalLoginActivity: {} as ActivityChartData,
        DataCollectionActivity: {} as ActivityChartData,
        TotalDataPointsByTypeEntered: {} as ActivityChartData,
        WorkshopActivity: {} as ActivityChartData,
        TopDistinctLoginsByDistrict: {} as ActivityChartData,
        TopNonDistinctLoginsByDistrict: {} as ActivityChartData,
        TopBehaviorCasesByType: {} as ActivityChartData,
        TotalCollectedPoints: {} as ActivityChartData,
        TopWorkshopsCompleted: {} as ActivityChartData,
        TopWorkshopsAccessed: {} as ActivityChartData,
        TopQuizzesPassed: {} as ActivityChartData,
        TopQuizzesAccessed: {} as ActivityChartData,
        TotalWorkshopsQuizzesCompleted: {} as ActivityChartData,
      };
      this.changeFilterActivity.emit(this.ActivityDashboardChartsData);
    }
  }

  updateWidgetStatus(payload: WidgetPayload) {
    this.activityDashboardService.updateWidgetStatus(payload).subscribe({
      next: response => {
        if (response?.result) {
          this.widgetList = this.widgetList.map(widget => {
            if (widget.id === payload.id) {
              widget = { ...widget, statusId: payload.StatusId };
              if (widget.statusId === 1) {
                if (
                  this.dashboardType ===
                  DashboardType.BehaviorManagementOverview
                ) {
                  this.processBehvaiorOverviewChartsData(widget);
                } else if (this.dashboardType === DashboardType.DistrictUsage) {
                  this.processDistricUsageChartsData(widget);
                } else if (
                  this.dashboardType ===
                  DashboardType.ELearningImplementationOverview
                ) {
                  this.processELEarningChartsData(widget);
                } else {
                  this.processActivityChartsData(widget);
                }
              }
              this.cdr.detectChanges();
            }
            return widget;
          });
        }
        this.cdr.detectChanges();
      },
      error: error => {
        this.loaderService.hide();
        this.commonService.notify('error', error.message);
      },
    });
  }

  updateWidgetOrders(dashboardType: number, payload: WidgetReorderPayload[]) {
    this.activityDashboardService
      .updateWidgetOrder(this.userData?.id ?? 0, dashboardType, payload)
      .subscribe({
        next: () => {
          this.cdr.detectChanges();
          this.loaderService.hide();
        },
        error: error => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  processBehvaiorOverviewChartsData(widget: ActivityDashboardWidget) {
    this.activityDashboardService
      .getBehaviorOverviewChartData({
        ChartType: widget.widgetId,
        OrganizationId: this.selectedOrg,
        StartDate: this.startDate,
        EndDate: this.endDate,
        UserSearch: this.userSearch,
        StudentSearch: this.studentSearch,
      })
      .subscribe({
        next: response => {
          if (response?.result) {
            widget.chartData = response.result;
            const chartTypeKey =
              DashboardBehaviorManagementComponent[widget.widgetId];
            this.BehaviorChartsData = {
              ...this.BehaviorChartsData,
              [chartTypeKey as keyof BehaviorChartsDataType]: response.result,
            };
          }
          widget.isLoading = false;
          this.changeFilter.emit(this.BehaviorChartsData);
        },
        error: error => {
          widget.isLoading = false;
          this.commonService.notify('error', error.message);
        },
        complete: () => {
          this.cdr.detectChanges();
        },
      });
  }

  getBehavoirHeaderStats() {
    const payload = {
      OrganizationId: this.selectedOrg,
      UserSearch: this.userSearch,
      StudentSearch: this.studentSearch,
      StartDate: this.startDate,
      EndDate: this.endDate,
    };
    this.loaderService.show();
    this.activityDashboardService.getBehaviorHeaderStats(payload).subscribe({
      next: response => {
        if (response?.result) {
          this.behaviorHeaderStats = response.result;
        }
        this.cdr.detectChanges();
      },
      error: error => {
        this.loaderService.hide();
        this.commonService.notify('error', error.message);
      },
    });
  }

  getBehaviorCaseList(
    pageSize: number,
    pageIndex: number,
    sort: string,
    sortOrder: number | undefined
  ) {
    const payload: DashboardBehaviorCaseListQueryParams = {
      OrganizationId: this.selectedOrg,
      UserSearch: this.userSearch,
      StudentSearch: this.studentSearch,
      StartDate: this.startDate,
      EndDate: this.endDate,
      PageSize: pageSize,
      Page: pageIndex,
      Sort: sort,
      SortOrder: sortOrder,
    };
    this.loaderService.show();
    this.activityDashboardService
      .getBehvaiorOverviewCaseList(payload)
      .subscribe({
        next: response => {
          if (response?.result) {
            this.behaviorCaseList = response.result.items;
            this.totalRecords = response.result.totalRows;
          }
          this.cdr.detectChanges();
          this.loaderService.hide();
        },
        error: error => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  getDistricUsageHeaderStats() {
    const payload = {
      OrganizationId: this.selectedOrg,
      StartDate: this.startDate,
      EndDate: this.endDate,
    };
    this.loaderService.show();
    this.activityDashboardService
      .getDistricUsageHeaderStats(payload)
      .subscribe({
        next: response => {
          if (response?.result) {
            this.districtUsageHeaderStats = response.result;
          }
          this.cdr.detectChanges();
        },
        error: error => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  getDistricUsageSummary() {
    const payload = {
      orgid: this.selectedOrg,
      startdate: this.startDate,
      enddate: this.endDate,
      type: 'district_summary',
    };
    this.loaderService.show();
    this.activityDashboardService.getDistricUsageSummary(payload).subscribe({
      next: response => {
        if (response?.data) {
          this.districUsageSummary = response.data;
        }
        this.cdr.detectChanges();
      },
      error: error => {
        this.loaderService.hide();
        this.commonService.notify('error', error.message);
      },
    });
  }

  processDistricUsageChartsData(widget: ActivityDashboardWidget) {
    if (
      widget.widgetId ===
        DashboardDistrictUsageComponent.HoursOfPDByOrganizationTop7 ||
      widget.widgetId ===
        DashboardDistrictUsageComponent.OrganizationsWithTopEnrollmentTop7
    ) {
      this.getDistricUsageLMSChartData(
        {
          orgid: this.selectedOrg,
          type: LmsChartType[widget.widgetId],
          startdate: this.startDate,
          enddate: this.endDate,
        },
        widget
      );
    } else {
      this.getDistricUsageChartData(
        {
          ChartType: widget.widgetId,
          OrganizationId: this.selectedOrg,
          StartDate: this.startDate,
          EndDate: this.endDate,
        },
        widget
      );
    }
  }

  getDistricUsageChartData(
    payload: ChartQueryParams,
    widget: ActivityDashboardWidget
  ) {
    this.activityDashboardService.getDistricUsageChartData(payload).subscribe({
      next: response => {
        if (response?.result) {
          const chartTypeKey =
            DashboardDistrictUsageComponent[payload.ChartType ?? 0];
          this.DistricUsageChartsData = {
            ...this.DistricUsageChartsData,
            [chartTypeKey as keyof DistricUsageChartsDataType]: response.result,
          };
        }
        this.changeFilter.emit(this.DistricUsageChartsData);
        this.cdr.detectChanges();
        widget.isLoading = false;
      },
      error: error => {
        widget.isLoading = false;
        this.commonService.notify('error', error.message);
      },
    });
  }

  getDistricUsageLMSChartData(
    payload: DistrictUsageSummaryPayload,
    widget: ActivityDashboardWidget
  ) {
    this.activityDashboardService
      .getDistricUsageLMSChartData(payload)
      .subscribe({
        next: response => {
          if (response?.data) {
            const chartTypeKey =
              DashboardDistrictUsageComponent[widget.widgetId ?? 0];
            this.DistricUsageChartsData = {
              ...this.DistricUsageChartsData,
              [chartTypeKey as keyof DistricUsageChartsDataType]: response.data,
            };
          }
          this.changeFilter.emit(this.DistricUsageChartsData);
          this.cdr.detectChanges();
          this.loaderService.hide();
          widget.isLoading = false;
        },
        error: error => {
          this.loaderService.hide();
          widget.isLoading = false;
          this.commonService.notify('error', error.message);
        },
      });
  }

  getDistrictUsageCaseList(
    pageSize: number,
    pageIndex: number,
    sort: string,
    sortOrder: number | undefined
  ) {
    const payload: DistrictUsageCaseListQueryParams = {
      OrganizationId: this.selectedOrg,
      StartDate: this.startDate,
      EndDate: this.endDate,
      PageSize: pageSize,
      Page: pageIndex,
      Sort: sort,
      SortOrder: sortOrder,
    };
    this.loaderService.show();
    this.activityDashboardService.getDistricUsageCaseList(payload).subscribe({
      next: response => {
        if (response?.result) {
          this.districtUsageCaseList = response.result.items;
          this.totalDistricRecords = response.result.totalRows;
        }
        this.loaderService.hide();
        this.cdr.detectChanges();
      },
      error: error => {
        this.loaderService.hide();
        this.commonService.notify('error', error.message);
      },
    });
  }

  getELEarningSummary() {
    const payload: DistrictUsageSummaryPayload = {
      orgid: this.selectedOrg,
      startdate: this.startDate,
      enddate: this.endDate,
      type: 'summary',
    };
    if (this.learnerSearch !== 0) {
      payload.userid = this.learnerSearch;
    }
    this.loaderService.show();
    this.activityDashboardService.getELearingSummary(payload).subscribe({
      next: response => {
        if (response?.data) {
          this.eLearningHeaderStats = response.data;
        }
        this.loaderService.hide();
        this.cdr.detectChanges();
      },
      error: error => {
        this.loaderService.hide();
        this.commonService.notify('error', error.message);
      },
    });
  }

  processELEarningChartsData(widget: ActivityDashboardWidget) {
    if (widget.widgetId === DashboardElearningComponent.CompletedWorkshops) {
      this.getELEarningWorkshopCompletedChartData(
        {
          orgid: this.selectedOrg,
          type: ELearningChartType['Completed'],
          startdate: this.startDate,
          enddate: this.endDate,
          userid: this.learnerSearch,
        },
        widget
      );
    } else {
      this.getELEarningLMSChartData(
        {
          orgid: this.selectedOrg,
          type:
            widget.widgetId ===
            DashboardElearningComponent.HoursOfPDByOrganizationTop7
              ? ELearningChartType['HoursSummary']
              : ELearningChartType['DeadlineSummary'],
          startdate: this.startDate,
          enddate: this.endDate,
          userid: this.learnerSearch,
        },
        widget
      );
    }
  }

  getELEarningLMSChartData(
    payload: DistrictUsageSummaryPayload,
    widget: ActivityDashboardWidget
  ) {
    this.activityDashboardService
      .getDistricUsageLMSChartData(payload)
      .subscribe({
        next: response => {
          if (response?.data) {
            const chartTypeKey =
              DashboardElearningComponent[widget.widgetId ?? 0];
            this.ELearningChartsData = {
              ...this.ELearningChartsData,
              [chartTypeKey as keyof ELearningChartDataType]: response.data,
            };
          }
          this.changeFilter.emit(this.ELearningChartsData);
          this.cdr.detectChanges();
          this.loaderService.hide();
          widget.isLoading = false;
        },
        error: error => {
          this.loaderService.hide();
          widget.isLoading = false;
          this.commonService.notify('error', error.message);
        },
      });
  }

  getELEarningWorkshopCompletedChartData(
    payload: DistrictUsageSummaryPayload,
    widget: ActivityDashboardWidget
  ) {
    this.getELEarningLMSChartData(payload, widget);
  }

  getELearningDataList(
    pageSize: number,
    pageIndex: number,
    sort: string,
    sortOrder: number | undefined
  ) {
    const defaultSort = 'org_name';
    const resolvedSort = sort?.trim() ? sort : defaultSort;
    const payload: ELearningDataListQueryParams = {
      orgid: this.selectedOrg,
      startdate: this.startDate,
      enddate: this.endDate,
      perpage: pageSize,
      page: pageIndex,
      orderBy: resolvedSort,
      orderByType: sortOrder ? 'desc' : 'asc',
    };
    if (this.learnerSearch !== 0) {
      payload.userid = this.learnerSearch;
    }
    this.loaderService.show();
    this.activityDashboardService.getELearingDataList(payload).subscribe({
      next: response => {
        if (response?.result) {
          this.eLearningDataList = response.result.items;
          this.totalELearningRecords = response.result.totalRows;
        }
        this.loaderService.hide();
        this.cdr.detectChanges();
      },
      error: error => {
        this.loaderService.hide();
        this.commonService.notify('error', error.message);
      },
    });
  }

  getActivityChartData(
    payload: ChartQueryParams,
    widget: ActivityDashboardWidget
  ) {
    this.activityDashboardService
      .getActivityDashBoardChartData(payload)
      .subscribe({
        next: response => {
          const chartTypeKey =
            DashboardActivityComponent[payload.ChartType ?? 0];

          if (response?.result) {
            this.ActivityDashboardChartsData = {
              ...this.ActivityDashboardChartsData,
              [chartTypeKey as keyof ActivityChartsDataType]: response.result,
            };
          } else {
            this.ActivityDashboardChartsData = {
              ...this.ActivityDashboardChartsData,
              [chartTypeKey as keyof ActivityChartsDataType]: {
                chartType: payload.ChartType ?? widget.widgetId,
                chartTitle: chartTypeKey ?? '',
                data: [],
              },
            };
          }
          this.changeFilterActivity.emit(this.ActivityDashboardChartsData);
          this.cdr.detectChanges();
          widget.isLoading = false;
        },
        error: error => {
          this.loaderService.hide();
          widget.isLoading = false;
          this.commonService.notify('error', error.message);
        },
      });
  }

  getLMSActivityChartData(
    payload: DistrictUsageSummaryPayload,
    widget: ActivityDashboardWidget
  ) {
    this.activityDashboardService.getActivityDashBoardData(payload).subscribe({
      next: response => {
        const chartTypeKey = DashboardActivityComponent[widget.widgetId ?? 0];
        if (response?.data) {
          const chartData = response.data;
          if (
            widget.widgetId === DashboardActivityComponent.WorkshopActivity &&
            (!chartData ||
              typeof chartData !== 'object' ||
              !(
                'chartType' in chartData &&
                'chartTitle' in chartData &&
                'data' in chartData
              ))
          ) {
            const formattedRes = {
              chartType: widget.widgetId,
              chartTitle: chartTypeKey || '',
              data: response.data,
            };
            this.ActivityDashboardChartsData = {
              ...this.ActivityDashboardChartsData,
              [chartTypeKey as keyof ActivityChartsDataType]: formattedRes,
            };
          } else {
            this.ActivityDashboardChartsData = {
              ...this.ActivityDashboardChartsData,
              [chartTypeKey as keyof ActivityChartsDataType]: chartData,
            };
          }
        } else {
          this.ActivityDashboardChartsData = {
            ...this.ActivityDashboardChartsData,
            [chartTypeKey as keyof ActivityChartsDataType]: {
              chartType: widget.widgetId,
              chartTitle: chartTypeKey ?? '',
              data: [],
            },
          };
        }
        this.changeFilterActivity.emit(this.ActivityDashboardChartsData);
        this.cdr.detectChanges();
        this.loaderService.hide();
        widget.isLoading = false;
      },
      error: error => {
        this.loaderService.hide();
        widget.isLoading = false;
        this.commonService.notify('error', error.message);
      },
    });
  }

  processActivityChartsData(widget: ActivityDashboardWidget) {
    if (this.lmsActivityChartTypes.includes(widget.widgetId)) {
      this.getLMSActivityChartData(
        {
          orgid: this.selectedOrg,
          type: DashboardActivityChartType[widget.widgetId],
          startdate: this.startDate,
          enddate: this.endDate,
        },
        widget
      );
    } else {
      this.getActivityChartData(
        {
          ChartType: widget.widgetId,
          OrganizationId: this.selectedOrg,
          OrganizationIds: [this.selectedOrg],
          StartDate: this.startDate,
          EndDate: this.endDate,
        },
        widget
      );
    }
  }
}
